import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { BaseElement, customElement, watch } from '../base-element';
import {
  SegmentedControlItemWC,
  tagName as itemTagName,
} from './segmented-control-item.wc';

import styles from './segmented-control.scss?inline';

/**
 * @summary A single-select control that lays out its options as equal-width
 * segments in a pill container (e.g. Sandbox / Live).
 *
 * @slot - One or more `<ps-segmented-control-item>` elements.
 * @event change - Emitted when the selected value changes. `detail.value` holds the new value.
 */
@customElement('ps-segmented-control')
export class SegmentedControlWC extends BaseElement {
  static styles = unsafeCSS(styles);

  /** The value of the currently selected item. */
  @property({ reflect: true }) value = '';

  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    this.setAttribute('role', 'radiogroup');
  }

  firstUpdated() {
    this.syncItems();
  }

  private getItems(): SegmentedControlItemWC[] {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot');
    return [...(slot?.assignedElements() ?? [])].filter(
      (el) => el.tagName.toLowerCase() === itemTagName
    ) as SegmentedControlItemWC[];
  }

  private syncItems = () => {
    this.getItems().forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      item.active = item.value === this.value;
    });
  };

  private selectItem(item: SegmentedControlItemWC) {
    if (item.disabled || item.value === this.value) {
      return;
    }
    this.value = item.value;
    this.emit('change', { detail: { value: this.value } });
  }

  private handleClick = (event: MouseEvent) => {
    const item = (event.target as HTMLElement).closest<SegmentedControlItemWC>(
      itemTagName
    );
    if (item) {
      this.selectItem(item);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) {
      return;
    }
    const items = this.getItems().filter((item) => !item.disabled);
    if (!items.length) {
      return;
    }
    event.preventDefault();
    const currentIndex = Math.max(
      items.findIndex((item) => item.value === this.value),
      0
    );
    const step = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
    const next = items[(currentIndex + step + items.length) % items.length];
    this.selectItem(next);
    next.focus();
  };

  @watch('value', { waitUntilFirstUpdate: true })
  handleValueChange() {
    this.syncItems();
  }

  render() {
    return html`
      <slot
        class="c-segmented-control"
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}
        @slotchange=${this.syncItems}
      ></slot>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-segmented-control': SegmentedControlWC;
  }
  enum PSElementTagNameMap {
    'ps-segmented-control' = 'ps-segmented-control',
  }
}
